import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Collapse } from '@mui/material';
import TimerIcon from '@material-ui/icons/Timer';
import moment from 'moment';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from '../../Button';
import ActionItemPriorityIndicator from './ActionItemPriorityIndicator';

const NAME_MAX_HEIGHT = 18;

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    flexBasis: '33%',
    minWidth: '25%',
    paddingRight: theme.spacing(1),
    alignItems: 'center',
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
    alignItems: 'center',
    display: 'flex',
    flexGrow: 2,
  },
  time: {
    alignSelf: 'center',
    color: theme.palette.primary.main,
    '&[completed=true]': {
      color: theme.palette.facet.mediumGray,
    },
    fontSize: '0.8em',
    display: 'flex',
    flexBasis: '25%',
  },
  wrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  nameContainer: {
  },
  nameCollapse: {
    display: 'flex',
    alignSelf: 'center',
    visibility: 'visible',
  },
  alignCenter: {
    alignSelf: 'center',
  },
  alignStart: {
    alignSelf: 'flex-start',
  },
  [theme.breakpoints.down('md')]: {
    root: {
      alignItems: 'flex-start',
      flexShrink: 1,
    },
    time: {
      justifyContent: 'flex-end',
    },
  },
});

function ListItemLabel({
  className,
  classes,
  resource,
  expand,
  fileExpand,
  setExpand,
}) {
  const ref = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const name = resource.attr('name');
  const dueDate = resource.attr('dueDate');
  const completed = resource.attr('status') === 'completed';
  const priority = resource.attr('priority');

  useEffect(() => {
    setHasOverflow(ref.current.clientHeight > NAME_MAX_HEIGHT);
  }, [ref]);

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.wrapper}>
        <ActionItemPriorityIndicator priority={priority} completed={completed} />
        <Collapse
          className={classNames(classes.nameCollapse)}
          collapsedSize={18}
          in={expand}
          timeout={{
            enter: 200,
            exit: 0,
          }}
          style={{ width: '100%' }}
        >
          <div ref={ref} align="left" color="primary" className={classes.name}>
            {name}
          </div>
        </Collapse>
        {hasOverflow && (
          <Button
            size="small"
            color="primary"
            onClick={() => setExpand(!expand)}
            variant="icon"
            className={classNames({
              [classes.alignCenter]: !expand,
              [classes.alignStart]: expand || fileExpand,
            })}
          >
            {expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        )}
        <span
          className={classNames(classes.time, {
            [classes.alignCenter]: !expand,
            [classes.alignStart]: expand || fileExpand,
          })}
          completed={String(completed)}
        >
          <TimerIcon fontSize="small" color="inherit" />
          {moment(dueDate).format('M/D')}
        </span>
      </div>
    </div>
  );
}

ListItemLabel.defaultProps = {
  className: '',
};

ListItemLabel.propTypes = {
  resource: PropTypes.object.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  expand: PropTypes.bool.isRequired,
  fileExpand: PropTypes.bool.isRequired,
  setExpand: PropTypes.func.isRequired,
};

export default withStyles(styles)(ListItemLabel);
